/**
 * useNotifications Hook
 * One screen's notification list. The unread count, the live connection and the mutations come
 * from NotificationsProvider, whose events keep every screen's rows in step.
 */

import { useAuth } from '@contexts/AuthContext';
import { type NotificationsEvent, useNotificationsOwner } from '@contexts/NotificationsContext';
import type { Notification } from '@parlay-picker/shared/notification';
import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config/api';

// =============================================================================
// Types
// =============================================================================

export interface FetchNotificationsParams {
  limit?: number;
  offset?: number;
  types?: string[];
  status?: 'read' | 'unread' | 'all';
  /** When true, append to existing notifications instead of replacing */
  append?: boolean;
}

export interface UseNotificationsResult {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  isConnected: boolean;
  hasMore: boolean;
  total: number;
  /** Fetch the notification list with optional filtering + pagination */
  fetchNotifications: (params?: FetchNotificationsParams) => Promise<void>;
  /** Load the next page of notifications */
  loadMore: () => Promise<void>;
  /** Mark specific notification IDs as read */
  markAsRead: (ids: string[]) => Promise<void>;
  /** Mark all notifications as read */
  markAllAsRead: () => Promise<void>;
  /** Dismiss (delete) a single notification */
  dismiss: (id: string) => Promise<void>;
}

/** Whether a live notification belongs in a list last loaded with these filters */
function matchesListFilter(notification: Notification, params: FetchNotificationsParams): boolean {
  if (params.types && params.types.length > 0 && !params.types.includes(notification.type)) {
    return false;
  }
  if (params.status === 'read') return !!notification.readAt;
  if (params.status === 'unread') return !notification.readAt;
  return true;
}

function applyEvent(rows: Notification[], event: NotificationsEvent): Notification[] {
  switch (event.kind) {
    case 'arrived':
      return rows.some((n) => n.id === event.notification.id)
        ? rows
        : [event.notification, ...rows];
    case 'read':
      return rows.map((n) =>
        event.ids.includes(n.id) && !n.readAt ? { ...n, readAt: event.readAt } : n,
      );
    case 'readAll':
      return rows.map((n) => (n.readAt ? n : { ...n, readAt: event.readAt }));
    case 'dismissed':
      return rows.filter((n) => n.id !== event.id);
  }
}

// =============================================================================
// Hook
// =============================================================================

export function useNotifications(): UseNotificationsResult {
  const { authFetch } = useAuth();
  const {
    unreadCount,
    isConnected,
    markAsRead: ownerMarkAsRead,
    markAllAsRead,
    dismiss: ownerDismiss,
    refreshUnreadCount,
    subscribe,
  } = useNotificationsOwner();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  /** The rows as of the last change, which the owner reads when it counts a mark or dismiss */
  const rowsRef = useRef<Notification[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  /** Track the last fetch params for loadMore */
  const lastFetchParamsRef = useRef<FetchNotificationsParams>({});

  const updateRows = useCallback((update: (rows: Notification[]) => Notification[]) => {
    rowsRef.current = update(rowsRef.current);
    setNotifications(rowsRef.current);
  }, []);

  useEffect(
    () =>
      subscribe((event) => {
        if (
          event.kind === 'arrived' &&
          !matchesListFilter(event.notification, lastFetchParamsRef.current)
        ) {
          return;
        }
        updateRows((rows) => applyEvent(rows, event));
      }),
    [subscribe, updateRows],
  );

  // ─── Fetch notification list with optional filtering + pagination ────
  const fetchNotifications = useCallback(
    async (params: FetchNotificationsParams = {}) => {
      setLoading(true);
      try {
        const queryParts: string[] = [];
        if (params.limit != null) queryParts.push(`limit=${params.limit}`);
        if (params.offset != null) queryParts.push(`offset=${params.offset}`);
        if (params.types && params.types.length > 0)
          queryParts.push(`types=${params.types.join(',')}`);
        if (params.status) queryParts.push(`status=${params.status}`);
        const qs = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';

        const res = await authFetch(`${API_BASE_URL}/api/notifications${qs}`);
        if (res.ok) {
          const data = await res.json();
          const fetched: Notification[] = data.notifications ?? [];

          updateRows((rows) => (params.append ? [...rows, ...fetched] : fetched));

          setTotal(data.total ?? fetched.length);
          setHasMore(data.hasMore ?? false);

          // A page holds at most one page of unread rows, so an unfiltered load re-reads the total
          if (!params.append && !params.types && !params.status) {
            refreshUnreadCount();
          }

          // Store params for loadMore (exclude append and offset)
          lastFetchParamsRef.current = {
            limit: params.limit,
            types: params.types,
            status: params.status,
          };
        }
      } catch {
        // Silently fail
      } finally {
        setLoading(false);
      }
    },
    [authFetch, updateRows, refreshUnreadCount],
  );

  // ─── Load more (next page) ─────────────────────────────────────────
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchNotifications({
      ...lastFetchParamsRef.current,
      offset: notifications.length,
      append: true,
    });
  }, [hasMore, loading, notifications.length, fetchNotifications]);

  const markAsRead = useCallback(
    (ids: string[]) =>
      ownerMarkAsRead(
        ids,
        () => rowsRef.current.filter((n) => ids.includes(n.id) && !n.readAt).length,
      ),
    [ownerMarkAsRead],
  );

  const dismiss = useCallback(
    (id: string) => ownerDismiss(id, () => rowsRef.current.some((n) => n.id === id && !n.readAt)),
    [ownerDismiss],
  );

  return {
    notifications,
    unreadCount,
    loading,
    isConnected,
    hasMore,
    total,
    fetchNotifications,
    loadMore,
    markAsRead,
    markAllAsRead,
    dismiss,
  };
}
